import { useState } from "react";
import { Download, ShieldCheck, ShieldAlert, Loader2, Archive } from "lucide-react";
import toast from "react-hot-toast";
import {
  useAuditLogs,
  exportAuditLogsCsv,
  useAuditChainIntegrity,
  useAuditRetentionStatus,
  type AuditLogFilters,
} from "@/api/audit";
import { useAuthStore } from "@/stores/authStore";
import { getApiErrorMessage } from "@/api/client";

const ACTION_OPTIONS = [
  "LOGIN",
  "LOGOUT",
  "CREATE",
  "UPDATE",
  "DELETE",
  "DOWNLOAD",
  "UPLOAD",
  "APPROVE",
  "EXPORT",
  "PERMISSION_CHANGE",
  "ARCHIVE",
];

export default function AuditPage() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canExport = hasPermission("audit.export");

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [draftFilters, setDraftFilters] = useState<AuditLogFilters>({});
  const [exporting, setExporting] = useState(false);
  const [integrityEnabled, setIntegrityEnabled] = useState(false);

  const { data, isLoading } = useAuditLogs(page, filters);
  const logs = data?.data ?? [];
  const meta = data?.meta;

  const { data: integrity, isFetching: isVerifying, refetch: refetchIntegrity } = useAuditChainIntegrity(integrityEnabled);
  const { data: retention } = useAuditRetentionStatus();

  const applyFilters = () => {
    setPage(1);
    setFilters(draftFilters);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAuditLogsCsv(filters);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  const handleVerifyIntegrity = () => {
    if (!integrityEnabled) {
      setIntegrityEnabled(true);
    } else {
      void refetchIntegrity();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Auditoría</h1>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={handleVerifyIntegrity} disabled={isVerifying}>
            {isVerifying ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ShieldCheck size={14} className="mr-1.5" />}
            Verificar integridad
          </button>
          {canExport && (
            <button type="button" className="btn-secondary" onClick={handleExport} disabled={exporting}>
              <Download size={14} className="mr-1.5" /> {exporting ? "Exportando..." : "Exportar CSV"}
            </button>
          )}
        </div>
      </div>

      {integrityEnabled && integrity && (
        <div
          className={`card flex items-start gap-3 p-4 text-sm ${
            integrity.valid ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
          }`}
        >
          {integrity.valid ? (
            <ShieldCheck size={20} className="mt-0.5 shrink-0 text-green-600" />
          ) : (
            <ShieldAlert size={20} className="mt-0.5 shrink-0 text-red-600" />
          )}
          <div>
            <p className={`font-medium ${integrity.valid ? "text-green-800" : "text-red-800"}`}>
              {integrity.valid
                ? "La cadena de integridad del registro de auditoría es válida."
                : "Se detectó una inconsistencia en la cadena de integridad."}
            </p>
            <p className="mt-1 text-gray-600">
              {integrity.hashedRows} de {integrity.totalRows} registros participan de la cadena de verificación
              {integrity.unhashedRows > 0 && ` (${integrity.unhashedRows} son anteriores a esta función y no tienen hash)`}.
            </p>
            {!integrity.valid && integrity.firstBrokenRow && (
              <p className="mt-1 text-red-700">
                Primer registro afectado: {integrity.firstBrokenRow.id} ({new Date(integrity.firstBrokenRow.createdAt).toLocaleString()}
                ) — {integrity.firstBrokenRow.reason}
              </p>
            )}
          </div>
        </div>
      )}

      {retention && (
        <div className="card flex items-start gap-3 p-4 text-sm">
          <Archive size={20} className="mt-0.5 shrink-0 text-gray-500" />
          <div className="flex-1">
            <p className="font-medium text-gray-900">
              Retención de auditoría: {retention.retentionDays} días
              {!retention.archiveEnabled && " (archivado automático desactivado)"}
            </p>
            <p className="mt-1 text-gray-600">
              {retention.rowsPendingArchive > 0
                ? `${retention.rowsPendingArchive} registro(s) quedarían por fuera del período de retención si el archivado corriera hoy.`
                : "No hay registros por fuera del período de retención configurado."}
            </p>
            {retention.checkpoints.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                  Historial de archivados ({retention.checkpoints.length})
                </summary>
                <ul className="mt-2 space-y-1 text-gray-600">
                  {retention.checkpoints.map((c) => (
                    <li key={c.id}>
                      {new Date(c.createdAt).toLocaleString()} — {c.rowsArchived} filas archivadas (hasta{" "}
                      {new Date(c.archivedThroughDate).toLocaleDateString()})
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}

      <div className="card grid grid-cols-1 gap-3 p-4 sm:grid-cols-5">
        <select
          className="input"
          value={draftFilters.action ?? ""}
          onChange={(e) => setDraftFilters((f) => ({ ...f, action: e.target.value || undefined }))}
        >
          <option value="">Todas las acciones</option>
          {ACTION_OPTIONS.map((action) => (
            <option key={action} value={action}>
              {action}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="Tipo de entidad (ej: Client)"
          value={draftFilters.entityType ?? ""}
          onChange={(e) => setDraftFilters((f) => ({ ...f, entityType: e.target.value || undefined }))}
        />
        <input
          type="date"
          className="input"
          value={draftFilters.dateFrom ?? ""}
          onChange={(e) => setDraftFilters((f) => ({ ...f, dateFrom: e.target.value || undefined }))}
        />
        <input
          type="date"
          className="input"
          value={draftFilters.dateTo ?? ""}
          onChange={(e) => setDraftFilters((f) => ({ ...f, dateTo: e.target.value || undefined }))}
        />
        <div className="flex gap-2">
          <button type="button" className="btn-primary flex-1" onClick={applyFilters}>
            Filtrar
          </button>
          <button type="button" className="btn-secondary" onClick={clearFilters}>
            Limpiar
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Usuario</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Acción</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Entidad</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && logs.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Sin registros de auditoría todavía.
                </td>
              </tr>
            )}
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="px-4 py-3 text-gray-600">{new Date(log.createdAt).toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">{log.user?.fullName ?? "—"}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{log.action}</td>
                <td className="px-4 py-3 text-gray-600">
                  {log.entityType}
                  {log.entityId ? ` (${log.entityId.slice(0, 8)}…)` : ""}
                </td>
                <td className="px-4 py-3 text-gray-400">{log.ipAddress ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages}
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button
                className="btn-secondary"
                disabled={!!meta.totalPages && page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
